import zipcodes from 'zipcodes'
import type { TrainingLocation } from '@/api/types/enrollment'
import type { EligibilityFormData } from '../types'
import { normalizeStateCode, normalizeStateName } from '../states'
import {
  buildLocationOptions,
  getEffectiveLocationSelectionValue,
  getLocationSelectionPlaceholder
} from './helpers'

export function getLocationCoordinates(zipCode: string) {
  const zipInfo = zipCode ? zipcodes.lookup(zipCode) : null

  return {
    latitude: zipInfo?.latitude ?? null,
    longitude: zipInfo?.longitude ?? null
  }
}

export function getLocationDependentFieldReset() {
  return {
    endorsements: [],
    trainingStartDate: '',
    yardDays: [],
    trainingMode: undefined,
    hasLaptop: undefined
  } satisfies Partial<EligibilityFormData>
}

export function getLocationStoreFields({
  storedProgramClassOnly,
  storedProgramExternalId,
  storedProgramYardOnly,
  value
}: {
  storedProgramClassOnly: boolean
  storedProgramExternalId: string
  storedProgramYardOnly: boolean
  value: string
}) {
  return {
    locationExternalId: value,
    programExternalId: storedProgramExternalId,
    programYardOnly: storedProgramYardOnly,
    programClassOnly: storedProgramClassOnly
  }
}

export function getSelectedLocationState(activeLocations: TrainingLocation[], value: string) {
  const selected = activeLocations.find(
    location => (location.externalId || String(location.id)) === value
  )

  return selected?.state ?? ''
}

export function getLocationSelectionState({
  formData,
  isError,
  isLoadingLocations,
  isLocationLocked,
  isPending,
  locations,
  storedLicenseState,
  storedLocationExternalId
}: {
  formData: EligibilityFormData
  isError: boolean
  isLoadingLocations: boolean
  isLocationLocked: boolean
  isPending: boolean
  locations: TrainingLocation[]
  storedLicenseState: string
  storedLocationExternalId: string
}) {
  const driverLicenseStateCode = normalizeStateCode(
    formData.driverLicenseState || storedLicenseState
  )
  const driverLicenseStateName = normalizeStateName(driverLicenseStateCode)
  const isMissingDriverLicenseState = !isLocationLocked && driverLicenseStateCode.length === 0
  const activeLocations = locations.filter(location => location.active)
  const locationsInLicenseState = activeLocations.filter(
    location => normalizeStateCode(location.state) === driverLicenseStateCode
  )
  const effectiveLocation = getEffectiveLocationSelectionValue(formData, storedLocationExternalId)
  const assignedLocation = activeLocations.find(
    location => (location.externalId || String(location.id)) === effectiveLocation
  )
  const selectableLocations =
    isLocationLocked && assignedLocation ? [assignedLocation] : locationsInLicenseState
  const hasNoLocations =
    !isPending &&
    !isError &&
    (activeLocations.length === 0 || (isLocationLocked && !assignedLocation))
  const hasNoLicenseStateLocations =
    !isLocationLocked &&
    !isPending &&
    !isError &&
    activeLocations.length > 0 &&
    locationsInLicenseState.length === 0
  const isLocationSelectDisabled =
    isMissingDriverLicenseState ||
    isLoadingLocations ||
    isError ||
    hasNoLocations ||
    hasNoLicenseStateLocations

  const locationOptions = buildLocationOptions(selectableLocations)
  const selectedLocation = locationOptions.find(location => location.value === effectiveLocation)
  const selectedTransmission = selectedLocation?.transmission
  const bothAvailable = selectedTransmission === 'Automatic and Manual'
  const locationPlaceholder = getLocationSelectionPlaceholder({
    isMissingDriverLicenseState,
    isLoadingLocations,
    isError,
    hasNoLicenseStateLocations,
    hasNoLocations
  })

  return {
    activeLocations,
    bothAvailable,
    driverLicenseStateName,
    effectiveLocation,
    hasNoLicenseStateLocations,
    hasNoLocations,
    isLocationSelectDisabled,
    isMissingDriverLicenseState,
    locationOptions,
    locationPlaceholder,
    selectedLocation,
    selectedTransmission
  }
}
